import React, { useRef, useEffect } from 'react';

/**
 * BrandCard — Portfolio brand card with logo, category, description, value props, and link
 */
export default function BrandCard({
  category,
  name,
  logo,
  logoBg = 'white',
  logoSize = 'normal',   // 'normal' | 'large' — 'large' = 3× standard height
  description,
  valueProps = [],
  url,
  href,
  index = 0,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const logoBgMap = {
    dark:  { background: '#0B1422',  padding: '1.25rem 1.5rem' },
    light: { background: '#F3F4F6',  padding: '1.25rem 1.5rem' },
    white: { background: '#FFFFFF',  padding: '1.25rem 1.5rem', border: '1px solid var(--gray-200)' },
  };

  const logoContainerStyle = logoBgMap[logoBg] || logoBgMap.white;

  return (
    <article
      ref={ref}
      className="brand-card reveal"
      style={{ transitionDelay: `${index * 0.09}s` }}
      aria-label={`${name} — portfolio brand`}
    >
      {/* ── Logo ── */}
      {logo && (
        <div
          style={{
            ...logoContainerStyle,
            marginBottom: 'var(--sp-6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
            minHeight: logoSize === 'large' ? '180px' : '76px',
          }}
        >
          <img
            src={logo}
            alt={`${name} logo`}
            style={{
              maxHeight: logoSize === 'large' ? '168px' : '56px',
              maxWidth:  logoSize === 'large' ? '100%'  : '240px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
      )}

      {/* ── Category badge ── */}
      {category && (
        <div className="brand-card__badge">{category}</div>
      )}

      {/* ── Brand name fallback (only when no logo) ── */}
      {!logo && (
        <h3 className="brand-card__name">{name}</h3>
      )}

      {/* ── Description ── */}
      {description && (
        <p className="brand-card__desc">{description}</p>
      )}

      {/* ── Value propositions ── */}
      {valueProps.length > 0 && (
        <ul className="brand-card__props" aria-label={`${name} value propositions`}>
          {valueProps.map((prop, i) => (
            <li key={i} className="brand-card__prop">{prop}</li>
          ))}
        </ul>
      )}

      {/* ── External link ── */}
      {url && (
        <a
          href={href || `https://${url}`}
          target="_blank"
          rel="noreferrer noopener"
          className="brand-card__link"
          aria-label={`Visit ${name} at ${url}`}
        >
          Visit {url}
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path
              d="M3 8h10M9 4l4 4-4 4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      )}
    </article>
  );
}
